/**
 * Shared autocomplete module
 * Provides reusable autocomplete/autosuggest functionality for any search input
 * that has a `data-autocomplete-url` attribute.
 */

/**
 * Debounce function to limit API calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(func, wait) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}

/**
 * Highlight matching text in a suggestion label by wrapping the typed query
 * in <strong> tags. The matched portion is bold, the rest is normal weight.
 * @param {string} label - The suggestion label
 * @param {string} query - The user's typed query
 * @returns {string} HTML string with highlighted text
 */
function highlightMatch(label, query) {
	if (!query) return escapeHtml(label);

	const lowerLabel = label.toLowerCase();
	const lowerQuery = query.toLowerCase();
	const matchIndex = lowerLabel.indexOf(lowerQuery);

	if (matchIndex === -1) return escapeHtml(label);

	const before = label.slice(0, matchIndex);
	const match = label.slice(matchIndex, matchIndex + query.length);
	const after = label.slice(matchIndex + query.length);

	return `${escapeHtml(before)}<strong>${escapeHtml(match)}</strong>${escapeHtml(after)}`;
}

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
	const div = document.createElement("div");
	div.appendChild(document.createTextNode(str));
	return div.innerHTML;
}

/**
 * Initialize autocomplete on a given input element.
 * @param {object} options
 * @param {HTMLInputElement} options.input - The search input element (must have data-autocomplete-url)
 * @param {HTMLElement} options.container - The element to append the dropdown to (should be position:relative)
 * @param {HTMLFormElement} options.form - The form element (used for auto-submit)
 * @param {string} options.dropdownClass - CSS class for the dropdown container
 * @param {string} options.itemClass - CSS class for dropdown items
 * @returns {{ destroy: () => void }} Cleanup function
 */
export function initAutocomplete({
	input,
	container,
	form,
	dropdownClass = "Autocomplete",
	itemClass = "AutocompleteItem",
}) {
	const autocompleteUrl = input.getAttribute("data-autocomplete-url");
	if (!autocompleteUrl) return { destroy() {} };

	let autocompleteDropdown = null;
	let activeIndex = -1;
	let currentQuery = "";

	/**
	 * Lazily create the dropdown element and append it to the container.
	 * @returns {HTMLDivElement} The dropdown element.
	 */
	function createDropdown() {
		if (!autocompleteDropdown) {
			autocompleteDropdown = document.createElement("div");
			autocompleteDropdown.className = `${dropdownClass}`;
			autocompleteDropdown.setAttribute("role", "listbox");
			container.appendChild(autocompleteDropdown);
		}
		return autocompleteDropdown;
	}

	/**
	 * Make the dropdown visible and update aria-expanded on the input.
	 */
	function showDropdown() {
		const dropdown = createDropdown();
		dropdown.classList.add(`${dropdownClass}--visible`);
		input.setAttribute("aria-expanded", "true");
	}

	/**
	 * Hide the dropdown, clear the active item, and update aria-expanded.
	 */
	function hideDropdown() {
		if (autocompleteDropdown) {
			autocompleteDropdown.classList.remove(`${dropdownClass}--visible`);
			input.setAttribute("aria-expanded", "false");
			activeIndex = -1;
		}
	}

	/**
	 * Mark the dropdown item at the given index as active and scroll it into view.
	 * @param {number} index - Zero-based index of the item to activate.
	 */
	function setActiveItem(index) {
		const items = autocompleteDropdown?.querySelectorAll(`.${itemClass}`);
		if (!items || items.length === 0) return;

		items.forEach((item) => {
			item.classList.remove(`${itemClass}--active`);
			item.setAttribute("aria-selected", "false");
		});

		if (index >= 0 && index < items.length) {
			activeIndex = index;
			items[index].classList.add(`${itemClass}--active`);
			items[index].setAttribute("aria-selected", "true");
			items[index].scrollIntoView({ block: "nearest" });
		}
	}

	/**
	 * Apply the chosen suggestion: fill the input, hide the dropdown, then
	 * navigate to the suggestion URL or submit the form.
	 * @param {{ value: string, url?: string }} suggestion - The selected suggestion.
	 */
	function selectSuggestion(suggestion) {
		input.value = suggestion.value;
		hideDropdown();

		if (suggestion.url) {
			window.location.href = suggestion.url;
		} else {
			form.submit();
		}
	}

	/**
	 * Render the suggestion list inside the dropdown. Hides the dropdown when
	 * the list is empty.
	 * @param {Array<{ value: string, url?: string }>} suggestions
	 */
	function renderSuggestions(suggestions) {
		const dropdown = createDropdown();
		dropdown.innerHTML = "";
		activeIndex = -1;

		if (!suggestions || suggestions.length === 0) {
			hideDropdown();
			return;
		}

		suggestions.forEach((suggestion, index) => {
			const item = document.createElement("a");
			item.className = `${itemClass}`;
			item.setAttribute("role", "option");
			item.setAttribute("data-index", index);
			item.href = suggestion.url || "#";

			const textSpan = document.createElement("span");
			textSpan.className = `${itemClass}Text`;
			textSpan.innerHTML = highlightMatch(suggestion.value, currentQuery);

			item.appendChild(textSpan);

			item.addEventListener("click", (e) => {
				e.preventDefault();
				selectSuggestion(suggestion);
			});

			item.addEventListener("mouseenter", () => {
				setActiveItem(index);
			});

			dropdown.appendChild(item);
		});

		showDropdown();
	}

	/**
	 * Fetch suggestions for the given query and render them. Hides the dropdown
	 * on network errors.
	 * @param {string} query - The user's typed query.
	 * @returns {Promise<void>}
	 */
	async function fetchSuggestions(query) {
		currentQuery = query;

		try {
			const response = await fetch(
				`${autocompleteUrl}?q=${encodeURIComponent(query)}`,
			);
			if (!response.ok) {
				throw new Error("Network response was not ok");
			}
			const data = await response.json();
			renderSuggestions(data);
		} catch (error) {
			console.error("Autocomplete fetch error:", error);
			hideDropdown();
		}
	}

	const debouncedFetch = debounce(fetchSuggestions, 500);

	/**
	 * Handle input events on the search field, debouncing the suggestion fetch.
	 * Hides the dropdown for queries shorter than two characters.
	 * @param {Event} e - The input event.
	 */
	function onInput(e) {
		const query = e.target.value.trim();

		if (query.length < 2) {
			hideDropdown();
			return;
		}

		debouncedFetch(query);
	}

	/**
	 * Handle keyboard navigation inside the dropdown (arrows, Enter, Escape).
	 * @param {KeyboardEvent} e - The keydown event.
	 */
	function onKeydown(e) {
		const items = autocompleteDropdown?.querySelectorAll(`.${itemClass}`);
		if (!items || items.length === 0) return;

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				if (activeIndex < items.length - 1) {
					setActiveItem(activeIndex + 1);
				} else {
					setActiveItem(0);
				}
				break;

			case "ArrowUp":
				e.preventDefault();
				if (activeIndex > 0) {
					setActiveItem(activeIndex - 1);
				} else {
					setActiveItem(items.length - 1);
				}
				break;

			case "Enter":
				if (activeIndex >= 0 && activeIndex < items.length) {
					e.preventDefault();
					items[activeIndex].click();
				}
				break;

			case "Escape":
				e.preventDefault();
				hideDropdown();
				break;
		}
	}

	/**
	 * Hide the dropdown when the user clicks outside the autocomplete container.
	 * @param {MouseEvent} e - The document click event.
	 */
	function onClickOutside(e) {
		if (!container.contains(e.target)) {
			hideDropdown();
		}
	}

	/**
	 * Hide the dropdown when the form is submitted (e.g. via the search button).
	 */
	function onFormSubmit() {
		hideDropdown();
	}

	// Bind events
	input.addEventListener("input", onInput);
	input.addEventListener("keydown", onKeydown);
	document.addEventListener("click", onClickOutside);
	form.addEventListener("submit", onFormSubmit);

	// Return cleanup function
	return {
		destroy() {
			input.removeEventListener("input", onInput);
			input.removeEventListener("keydown", onKeydown);
			document.removeEventListener("click", onClickOutside);
			form.removeEventListener("submit", onFormSubmit);
			if (autocompleteDropdown) {
				autocompleteDropdown.remove();
			}
		},
	};
}
